import React from 'react';
import c from 'classnames';

import Stack from '../../Stack';
import Typography from '../../Typography';
import Avatar from '../Avatar';
import { avatarGroupStyles, avatarMoreStyle, borderSizeStyle } from './styles';
import { AvatarGroupProps } from './types';
import { DEFAULT_BORDER, getAvatarGroupConfig } from './utils';

const AvatarGroup: React.FC<React.PropsWithChildren<AvatarGroupProps>> = ({
  avatars,
  classes,
  className,
  disabled,
  maxCount = 4,
  size = 'small',
  extraCount,
}) => {
  const { borderSize, label, margin, size: avatarSize } = getAvatarGroupConfig(size);
  const borderClassname = classes?.border || DEFAULT_BORDER;

  if (!avatars.length) {
    return null;
  }

  if (maxCount && avatars.length > maxCount) {
    const updatedAvatar = avatars.slice(0, maxCount - 1);
    const moreCount = avatars.length - maxCount + 1;
    return (
      <Stack className={c(avatarGroupStyles(margin), className)}>
        {updatedAvatar.map((avatar, index) => (
          <Avatar
            key={index}
            className={c(borderClassname, 'avatar-root', borderSizeStyle(borderSize))}
            disabled={disabled}
            name={avatar.name ?? ''}
            size={avatarSize}
            src={avatar.src ?? ''}
          />
        ))}
        <Stack
          alignItems="center"
          className={c(
            'avatar-root rounded-full z-10',
            avatarMoreStyle(avatarSize),
            'bg-bg-tertiary',
            borderSizeStyle(borderSize),
            borderClassname,
          )}
          justifyContent="center">
          <Typography color="text-text-placeholder" variant={label}>
            {moreCount}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  if (extraCount && maxCount && avatars.length < maxCount) {
    return (
      <Stack className={c(avatarGroupStyles(margin), className)}>
        {avatars.map((avatar, index) => (
          <Avatar
            key={index}
            className={c(borderClassname, 'avatar-root', borderSizeStyle(borderSize))}
            disabled={disabled}
            name={avatar.name ?? ''}
            size={avatarSize}
            src={avatar.src ?? ''}
          />
        ))}
        <Stack
          alignItems="center"
          className={c(
            'avatar-root rounded-full z-10',
            avatarMoreStyle(avatarSize),
            'bg-bg-tertiary',
            borderSizeStyle(borderSize),
            borderClassname,
          )}
          justifyContent="center">
          <Typography color="text-text-placeholder" variant={label}>
            {extraCount}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack className={c(avatarGroupStyles(margin), className)}>
      {avatars.map((avatar, index) => (
        <Avatar
          children={avatar.children}
          key={index}
          className={c(borderClassname, borderSizeStyle(borderSize), 'avatar-root')}
          disabled={disabled}
          name={avatar.name ?? ''}
          size={avatarSize}
          src={avatar.src ?? ''}
        />
      ))}
    </Stack>
  );
};

export default React.memo(AvatarGroup);
